/**
 * Harvest Operations API Module
 * Uses exact same patterns as working CRM API
 */

import { type RequestFunction } from './types'
import type {
  HarvestPlan,
  HarvestPlanCreateInput,
  HarvestPlanUpdateInput,
  Container,
  ContainerCreateInput,
  Pallet,
  PalletCreateInput,
  PalletUpdateInput,
  MaterialTransfer,
  MaterialTransferCreateInput,
  PalletQRCodeResponse,
  PalletScanRequest,
  BulkLabelRequest,
  PaginatedHarvestPlans,
  PaginatedContainers,
  PaginatedPallets,
  FilterParams,
} from './types'

export class HarvestApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // HARVEST PLANS MANAGEMENT (Fixed to match CRM patterns)
  // ============================================================================

  async getHarvestPlans(params?: FilterParams & { farm?: number }): Promise<PaginatedHarvestPlans> {
    return this.request('/api/harvest-plans/', { params })
  }

  async createHarvestPlan(data: HarvestPlanCreateInput): Promise<HarvestPlan> {
    return this.request('/api/harvest-plans/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async getHarvestPlan(id: number): Promise<HarvestPlan> {
    return this.request(`/api/harvest-plans/${id}/`)
  }

  async updateHarvestPlan(id: number, data: HarvestPlanUpdateInput): Promise<HarvestPlan> {
    return this.request(`/api/harvest-plans/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async deleteHarvestPlan(id: number): Promise<void> {
    return this.request(`/api/harvest-plans/${id}/`, {
      method: 'DELETE'
    })
  }

  async getHarvestPlanContainers(harvestPlanId: number): Promise<Container[]> {
    return this.request(`/api/harvest-plans/${harvestPlanId}/containers/`)
  }

  // ============================================================================
  // CONTAINERS MANAGEMENT (Fixed to match CRM patterns)
  // ============================================================================

  async getContainers(params?: FilterParams & { harvest_plan?: number, is_rnd?: boolean }): Promise<PaginatedContainers> {
    return this.request('/api/containers/', { params })
  }

  async createContainer(data: ContainerCreateInput): Promise<Container> {
    return this.request('/api/containers/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async getContainer(id: number): Promise<Container> {
    return this.request(`/api/containers/${id}/`)
  }

  async updateContainer(id: number, data: Partial<ContainerCreateInput>): Promise<Container> {
    return this.request(`/api/containers/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async getContainerPallets(containerId: number): Promise<Pallet[]> {
    return this.request(`/api/containers/${containerId}/pallets/`)
  }

  // ============================================================================
  // PALLETS MANAGEMENT (Fixed to match CRM patterns)
  // ============================================================================

  async getPallets(params?: FilterParams & { container?: number, location?: number, status?: string }): Promise<PaginatedPallets> {
    return this.request('/api/pallets/', { params })
  }

  async createPallet(data: PalletCreateInput): Promise<Pallet> {
    return this.request('/api/pallets/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async createPalletInContainer(containerId: number, data: PalletCreateInput): Promise<Pallet> {
    const palletData = { ...data, container: containerId }
    return this.createPallet(palletData)
  }

  async getPallet(id: number): Promise<Pallet> {
    return this.request(`/api/pallets/${id}/`)
  }

  async updatePallet(id: number, data: PalletUpdateInput): Promise<Pallet> {
    return this.request(`/api/pallets/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  // ============================================================================
  // LOCATIONS MANAGEMENT
  // ============================================================================

  async getHarvestLocations(): Promise<Location[]> {
    return this.request('/api/harvest-plans/locations/')
  }

  // ============================================================================
  // QR CODE OPERATIONS (Fixed to match CRM patterns)
  // ============================================================================

  async generatePalletQRCode(palletId: number): Promise<PalletQRCodeResponse> {
    return this.request(`/api/pallets/${palletId}/qr-code/`)
  }

  async getPalletLabel(palletId: number, format: 'html' | 'pdf' = 'html'): Promise<any> {
    return this.request(`/api/pallets/${palletId}/label/?format=${format}`)
  }

  async scanQRCode(scanData: PalletScanRequest): Promise<Pallet> {
    return this.request('/api/pallets/scan/', {
      method: 'POST',
      body: JSON.stringify(scanData)
    })
  }

  // ============================================================================
  // LABEL GENERATION (Fixed to match CRM patterns)
  // ============================================================================

  async generatePalletLabel(palletId: number, format: 'pdf' | 'html' = 'html'): Promise<string | ArrayBuffer> {
    const endpoint = `/api/pallets/${palletId}/label/?format=${format}`
    return this.request(endpoint)
  }

  async generateBulkLabels(data: BulkLabelRequest): Promise<string | ArrayBuffer> {
    return this.request('/api/pallets/bulk-labels/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // ============================================================================
  // MATERIAL TRANSFERS (Fixed to match CRM patterns)
  // ============================================================================

  async createMaterialTransfer(data: MaterialTransferCreateInput): Promise<MaterialTransfer> {
    return this.request('/api/material-transfers/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async getMaterialTransferManifest(transferId: number): Promise<ArrayBuffer> {
    return this.request(`/api/material-transfers/${transferId}/manifest/`)
  }

  // ============================================================================
  // SUPPORTING DATA (Fixed to match CRM patterns)
  // ============================================================================

  async getStrains(): Promise<any[]> {
    return this.request('/api/strains/')
  }

  async getLocations(): Promise<any[]> {
    return this.request('/api/harvest-plans/locations/')
  }

  async getFarms(): Promise<any[]> {
    return this.request('/api/harvest-plans/farms/')
  }

  // ============================================================================
  // DASHBOARD METRICS (Fixed to match CRM patterns)
  // ============================================================================

  async getDashboardStats(): Promise<{
    active_plans: number;
    total_containers: number;
    total_pallets: number;
    recent_activity: Array<{
      id: string;
      type: string;
      title: string;
      description: string;
      timestamp: string;
    }>;
  }> {
    // For now, return empty stats since this endpoint might not exist yet
    return {
      active_plans: 0,
      total_containers: 0,
      total_pallets: 0,
      recent_activity: [],
    }
  }
}